import type { Persister } from '@tanstack/query-persist-client-core'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { createPersister } from '@/lib/cache/persister'
import { shouldPersistQuery } from '@/lib/cache/query-filter'
import { queryClient } from '@/lib/queryClient'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from '@tanstack/query-persist-client-core'
import { IsRestoringProvider, QueryClientProvider } from '@tanstack/react-query'

const MAX_AGE = 1000 * 60 * 5 // 5 minutes

/**
 * How old cached data can be before we skip restore entirely and just fetch fresh.
 * This prevents showing yesterday's data for 1-2 seconds on app open.
 * 5 minutes = if you closed the tab and reopened quickly, you get instant render.
 * Anything older = normal loading states, but with fresh data.
 */
const STALE_THRESHOLD = 1000 * 60 * 5 // 5 minutes

/**
 * Wraps a real persister to skip restore if the cached data is too stale.
 * Still persists on every save (so fresh data is always written), but on restore
 * it checks the timestamp and returns undefined if too old.
 */
function createFreshnessGuard(inner: Persister): Persister {
  return {
    persistClient: inner.persistClient,
    removeClient: inner.removeClient,
    restoreClient: async () => {
      const cached = await inner.restoreClient()
      if (!cached) return undefined

      const age = Date.now() - cached.timestamp
      if (age > STALE_THRESHOLD) {
        console.info(
          `[QueryCache] Skipping restore — cache is ${Math.round(age / 1000)}s old (threshold: ${STALE_THRESHOLD / 1000}s)`,
        )
        return undefined
      }

      console.info(
        `[QueryCache] Restoring cache — ${Math.round(age / 1000)}s old`,
      )
      return {
        ...cached,
        clientState: {
          ...cached.clientState,
          queries: cached.clientState.queries.filter(query =>
            shouldPersistQuery(query.queryKey),
          ),
        },
      }
    },
  }
}

export function QueryCacheProvider({ children }: { children: ReactNode }) {
  const userId = useAuth(s => s.user?.id)
  const orgId = useOrganizationStore(s => s.currentOrganization?.id)
  const [isRestoring, setIsRestoring] = useState(false)

  // Scope the cache per user AND org: query keys are org-agnostic (the org is
  // a request header), so an org-agnostic cache restored after an org switch
  // would serve the previous org's rows under identical query keys.
  const scopeKey = userId && orgId ? `${userId}:${orgId}` : undefined
  const prevScopeKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const prevScopeKey = prevScopeKeyRef.current
    prevScopeKeyRef.current = scopeKey

    // In-session user/org switch: the in-memory cache still holds the previous
    // scope's rows under org-agnostic query keys. Drop it all before restoring
    // or subscribing, otherwise those rows both render in the new org and get
    // persisted into the new org's storage bucket (which would then serve
    // cross-org data on the next boot). The org-switch removeQueries list only
    // covers known keys — this catches everything.
    if (prevScopeKey && scopeKey && prevScopeKey !== scopeKey) {
      void queryClient.cancelQueries()
      queryClient.removeQueries()
    }

    if (!scopeKey) {
      setIsRestoring(false)
      return
    }

    let cancelled = false
    let unsubscribe: (() => void) | undefined
    setIsRestoring(true)

    const initializePersistence = async () => {
      const createdPersister = await createPersister(scopeKey)
      if (cancelled || !createdPersister) return

      const options = {
        queryClient,
        persister: createFreshnessGuard(createdPersister),
        maxAge: MAX_AGE,
        buster: '',
        dehydrateOptions: {
          shouldDehydrateQuery: (query: {
            state: { status: string }
            queryKey: readonly unknown[]
          }) =>
            query.state.status === 'success' &&
            shouldPersistQuery(query.queryKey),
        },
      }

      await persistQueryClientRestore(options)
      if (cancelled) return

      unsubscribe = persistQueryClientSubscribe(options)
      // Background-refresh stale restored data. Subscribing first also ensures
      // the current cache is saved even when auth/org resolved after mount.
      await queryClient.invalidateQueries({ stale: true })
    }

    void initializePersistence()
      .catch((error: unknown) => {
        console.warn('[QueryCache] Failed to initialize persistence', error)
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false)
      })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [scopeKey])

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={isRestoring}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  )
}
